import { FieldTypes } from '../helpers/constants';
import { FieldValue } from '../types/form-types';
import { DataLayoutPosition, DataLayoutSection, DataLayoutTab, FieldPosition, TemplateField, TemplatePage } from '../types/template-types';
import { getSortedPages, getSortedTabs, getViewPosition, viewPositionSorter } from './components/FieldValidations/helpers/validationErrorsMenuHelpers';
import { FormEditorState, SketchValue } from './formeditor-types';
import { checkIfCalculationValueIsTrue } from './helpers';
import { getField } from './selectors';

export type ViewPosition = {
  field: TemplateField;
  page?: TemplatePage;
  fieldPosition?: FieldPosition;
  tab?: DataLayoutTab;
  section?: DataLayoutSection;
  dataLayoutPosition?: DataLayoutPosition;
  groupType: 'primary' | 'secondary' | 'unmapped';
  groupName: string;
};

/**
 * Checks to see if the form requirements are met.
 *
 * @param requiredField The required template field
 * @param values The fields on the form
 * @returns True, if the required field is filled, False otherwise.
 */
function checkRequiredField(requiredField: TemplateField, values: FieldValue[]) {
  const { type, id } = requiredField;
  const value = values ? values.find((v): boolean => v.fieldId === id) : null;

  if (!value || !value.value) {
    return false;
  }

  switch (type) {
    case FieldTypes.CheckBox:
      return value.value.toLowerCase() === 'false' ? false : true;

    // doing required field checks this value seem expensive compared to doing
    // it from within the component and setting nulls
    // another option could be to look for `"Value":"" | []`, but this seems
    // less reliable
    case FieldTypes.Sketch:
      const sketch = JSON.parse(value.value) as SketchValue;

      return !!sketch?.Value || !!sketch?.FileId;

    case FieldTypes.FileAttachment:
      const files = JSON.parse(value.value) as unknown[];

      return files?.length > 0;

    default:
      return true;
  }
}

export function getRequiredFields(state: FormEditorState) {
  const requiredFields: TemplateField[] = [];
  const fields = state.template?.fields;
  const formulaValues = state.formulaValues;
  if (fields) {
    for (const field of fields) {
      const formulaValueKey = `${field.id}_Required`;
      if (formulaValues && formulaValues[formulaValueKey]) {
        const formulaValue = formulaValues[formulaValueKey];
        if (checkIfCalculationValueIsTrue(formulaValue)) {
          requiredFields.push(field);
        }
      } else if (field.required) {
        requiredFields.push(field);
      }
    }
  }
  return requiredFields;
}

export type MissingRequiredField = {
  id: string;
  name: string;
};

export function getMissingRequiredFields(state: FormEditorState) {
  const isPublicForm = !!state.sharedAccessToken;
  const editableFields = isPublicForm ? getEditableFields(state) : [];
  const requiredFields = getRequiredFields(state);

  const values = state.form?.values;

  const missingRequiredFields: MissingRequiredField[] = [];
  for (const requiredField of requiredFields) {
    if (!checkRequiredField(requiredField, values) && (!isPublicForm || editableFields.some((fieldId) => fieldId === requiredField.id))) {
      missingRequiredFields.push({ id: requiredField.id, name: requiredField.name });
    }
  }

  return missingRequiredFields;
}

export function getEditableFields(state: FormEditorState) {
  const editableFieldIds = Object.keys(state.policyInfo?.fields ?? {});

  return editableFieldIds;
}

export const getEditableColumns = (state: FormEditorState) => {
  let editableColumnIds = [];
  const customFields = state.policyInfo?.fields;
  if (customFields) {
    for (const customField of Object.keys(customFields)) {
      const editableCustomField: TemplateField = customFields[customField];
      const customFieldColumns = editableCustomField.columns;
      if (customFieldColumns) {
        editableColumnIds = [...editableColumnIds, ...customFieldColumns.map((x) => x.id)];
      }
    }
  }
  return editableColumnIds;
};

export function getMissingRequiredFieldsViewPositions(state: FormEditorState) {
  const isPublicForm = !!state.sharedAccessToken;
  const fieldIds = state.missingRequiredFieldIds;
  if (!fieldIds || fieldIds.length === 0) {
    return [];
  }

  const visibleSortedPages = state.template.pages ? getSortedPages(state.template.pages, state.formulaValues) : [];
  const visibleSortedTabs =
    state.template.dataLayouts && state.template.dataLayouts.length > 0 && state.template.dataLayouts[0].dataLayoutTabs
      ? getSortedTabs(state.template.dataLayouts[0].dataLayoutTabs, state.formulaValues)
      : [];

  const viewPositions: ViewPosition[] = [];

  for (const fieldId of fieldIds) {
    const field = getField(state.template, fieldId);
    const viewPosition = getViewPosition(state.view, visibleSortedTabs, visibleSortedPages, field, state.formulaValues, isPublicForm);

    viewPositions.push(viewPosition);
  }

  return viewPositions.sort(viewPositionSorter);
}
